import React, { useState, useEffect, useCallback } from 'react';
import { dataRequestsAPI } from '../services/api';
import { FiRefreshCw, FiAlertTriangle, FiShield, FiPlus, FiX } from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

const TYPES = [
  ['ACCESS', 'Access'],
  ['RECTIFICATION', 'Rectification'],
  ['ERASURE', 'Erasure'],
  ['OBJECTION', 'Objection'],
  ['PORTABILITY', 'Portability'],
  ['OTHER', 'Other'],
];
const CHANNELS = [['EMAIL', 'Email'], ['FORM', 'Web form'], ['PHONE', 'Phone'], ['IN_PERSON', 'In person'], ['POST', 'Post']];
const STATUSES = [['RECEIVED', 'Received'], ['IN_PROGRESS', 'In Progress'], ['COMPLETED', 'Completed'], ['REJECTED', 'Rejected']];
const statusColor = (s) => ({ RECEIVED: '#f5a623', IN_PROGRESS: '#4facfe', COMPLETED: '#43e97b', REJECTED: '#94a3b8' }[s] || '#666');
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const NewRequestModal = ({ onSave, onCancel, loading }) => {
  const [f, setF] = useState({ subject_email: '', subject_name: '', request_type: 'ACCESS', channel: 'EMAIL', details: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 460 }}>
        <div className="modal-header"><h2 style={{ fontSize: 18 }}>Log a data-subject request</h2></div>
        <div className="modal-form">
          <div className="form-group">
            <label>Requester email *</label>
            <input value={f.subject_email} onChange={(e) => set('subject_email', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Name</label>
            <input value={f.subject_name} onChange={(e) => set('subject_name', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Request type *</label>
              <select value={f.request_type} onChange={(e) => set('request_type', e.target.value)}>
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Channel</label>
              <select value={f.channel} onChange={(e) => set('channel', e.target.value)}>
                {CHANNELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Details</label>
            <textarea rows={3} value={f.details} onChange={(e) => set('details', e.target.value)} />
          </div>
          <p style={{ color: '#94a3b8', fontSize: 12, margin: 0 }}>A 30-day response deadline is set automatically.</p>
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="btn-primary" onClick={() => onSave(f)} disabled={loading || !f.subject_email}>
            {loading ? 'Saving…' : 'Log request'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DataRequests = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await dataRequestsAPI.list();
      setRows(res.data.results || res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load requests.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (data) => {
    setBusy(true);
    try { await dataRequestsAPI.create(data); setShowNew(false); load(); }
    catch (e) { setError(JSON.stringify(e?.response?.data) || 'Failed to log request.'); }
    finally { setBusy(false); }
  };

  const patch = async (r, data) => {
    try { await dataRequestsAPI.update(r.id, data); load(); }
    catch { setError('Failed to update.'); }
  };

  const overdue = rows.filter((r) => r.is_overdue).length;

  return (
    <div className="students-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiShield size={22} color="#011ff3" /> Data-Subject Requests
          </h1>
          <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
            KVKK Art. 11 register — every access / rectification / erasure / objection request, tracked to resolution within 30 days.
            {overdue > 0 && <b style={{ color: '#f5576c' }}> {overdue} overdue.</b>}
          </p>
        </div>
        <div className="header-actions-right">
          <button className="btn-primary" onClick={() => setShowNew(true)}>
            <FiPlus size={15} /> Log request
          </button>
          <button className="btn-icon" onClick={load} title="Refresh">
            <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <FiAlertTriangle size={18} /><span style={{ flex: 1, wordBreak: 'break-word' }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty-state">No requests logged.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                {['Requester', 'Type', 'Received', 'Due', 'Status', 'Resolution'].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.subject_email}
                    {r.subject_name ? <div style={{ color: '#94a3b8', fontSize: 11 }}>{r.subject_name}</div> : null}
                    {r.student_reference ? <div style={{ color: '#011ff3', fontSize: 11 }}>{r.student_reference}</div> : null}
                  </td>
                  <td>{r.request_type_display}</td>
                  <td style={{ color: '#94a3b8' }}>{fmt(r.received_at)}</td>
                  <td style={{ color: r.is_overdue ? '#f5576c' : '#94a3b8', fontWeight: r.is_overdue ? 700 : 400 }}>
                    {fmt(r.due_at)}{r.is_overdue ? ' ⚠' : ''}
                  </td>
                  <td>
                    <select value={r.status} onChange={(e) => patch(r, { status: e.target.value })}
                      style={{ border: '1px solid ' + statusColor(r.status), color: statusColor(r.status), borderRadius: 8, padding: '4px 8px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: '#fff' }}>
                      {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td style={{ minWidth: 200 }}>
                    <input defaultValue={r.resolution_note} placeholder="What was done…"
                      onBlur={(e) => { if (e.target.value !== r.resolution_note) patch(r, { resolution_note: e.target.value }); }}
                      style={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: 6, padding: '5px 8px', fontSize: 12 }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && <NewRequestModal onSave={create} onCancel={() => setShowNew(false)} loading={busy} />}
    </div>
  );
};

export default DataRequests;
